import { FillGradient, type Graphics } from 'pixi.js';
import { PALETTE } from './palette';

/** The frame is traced from the board mock-up, where one cell measures 64.8 px. */
export const DESIGN_CELL = 64.8;
/** How far the frame reaches past the playfield, in mock-up px. */
export const FRAME_REACH = { side: 31, top: 33, bottom: 30 } as const;

type Pts = readonly number[];
type Stops = readonly (readonly [offset: number, color: number | string])[];

/**
 * Corner pieces are written for the top-left corner in mock-up px: u runs along the
 * top edge, v runs down the side, negative values sit outside the playfield.
 * Bottom pieces are traced with w growing downward and flipped into the same frame.
 */
const flip = (pts: Pts): number[] => pts.map((n, i) => (i % 2 ? -n : n));

const TOP_RAIL: Pts = [
  24, -9.5, 33, -18.5, 55, -18.5, 63, -26.5, 183, -26.5, 186.5, -23, 186.5, -20.5, 183, -20.5,
  172.5, -11, 171.5, -5, 156, -5, 153, -11.5, 148.5, -16, 81, -16, 72, -9.5,
];
const TOP_RAIL_LIT: Pts = [33, -17.6, 55, -17.6, 63, -25.6, 183, -25.6];
const TOP_SEAM: Pts = [57, -9.5, 74, -26.5];

const TOP_OUTER: Pts = [11.5, -19.5, 28.5, -19.5, -6, 15, -6, 50.5, -20.5, 50.5, -20.5, 12.5];
const TOP_OUTER_LIT: Pts = [-19.6, 12.5, 11.9, -18.6, 28.5, -18.6];
const TOP_LAMP_HOUSING: Pts = [-20.5, 12.5, -18.5, 10.5, -6, 10.5, -6, 50.5, -20.5, 50.5];
const TOP_INNER: Pts = [21, -1.5, 34, -1.5, 10, 24.5, 12.5, 26, 1.5, 37, -1.5, 37, -1.5, 21];

const BOTTOM_RAIL = flip([
  23.5, 5.5, 75, 5.5, 79, 9.5, 148, 9.5, 156, 0.5, 171, 0.5, 172, 7.5, 183, 17.5, 184.5, 19.5,
  184.5, 22, 62, 22, 52.5, 15.5, 33.5, 15.5,
]);
const BOTTOM_RAIL_LIT = flip([23.5, 6.4, 75, 6.4, 79, 10.4, 148, 10.4, 156, 1.4, 171, 1.4]);
const BOTTOM_SEAM = flip([55, 5.5, 71.5, 22]);

const BOTTOM_OUTER = flip([-18.5, -21, -6.5, -21, 28.5, 14, 28.5, 17, 13.5, 17, -18.5, -15]);
const BOTTOM_INNER = flip([
  -0.5, -47, 11, -40, 11, -31.5, 25.5, -17, 28.5, -12, 33.5, -1, 22.5, -1, -0.5, -24,
]);
const BOTTOM_INNER_LIT = flip([0.3, -45.8, 10.4, -39.2]);

/** Side trim runs down the left edge; v is measured from the top edge of the playfield. */
const BRACKET_TOP: Pts = [
  -10.5, 160.5, -14, 160.5, -23, 169.5, -23, 223, -9, 237, -7.5, 237, -7.5, 217.5, -10.5, 217.5,
];
const BRACKET_TOP_LIT: Pts = [-10.5, 161.4, -14.4, 161.4, -22.1, 169.5, -22.1, 223];
/** Lower bracket, measured upward from the bottom edge (w < 0). */
const BRACKET_LOW: Pts = [
  -7.5, -246.5, -22.5, -231.5, -22.5, -174, -16, -167.5, -10.5, -167.5, -10.5, -225, -7.5, -225,
];
const BRACKET_LOW_LIT: Pts = [-8.2, -245.6, -21.6, -231.5, -21.6, -174];
/** Side lamp housing around its centre. */
const SIDE_HOUSING: Pts = [-19, -34, -10, -26, -10, 27, -20, 36, -30, 28, -30, -27];
/** Lamp centre as a share of the side length. */
const SIDE_LAMP_AT = 0.4875;
/** Lamp glows as [u, v, radius]; the side glow is stretched vertically when drawn. */
const GLOW_TOP = [-13, 33, 26] as const;
const GLOW_BOTTOM = [-14, 45, 30] as const;
const GLOW_SIDE = [-19, 0, 22] as const;
const GLOW_SIDE_STRETCH = 2.2;

const GOLD_EDGE = 0x1e1406;
const TEAL_EDGE = 0x041820;

function linear(x0: number, y0: number, x1: number, y1: number, stops: Stops): FillGradient {
  // Pixi swaps per axis when a delta is negative, which skews diagonals; keep both deltas >= 0.
  return new FillGradient({
    type: 'linear',
    start: { x: x0, y: y0 },
    end: { x: x1, y: y1 },
    textureSpace: 'global',
    colorStops: stops.map(([offset, color]) => ({ offset, color })),
  });
}

function radial(x: number, y: number, r: number, stops: Stops, scale = 1): FillGradient {
  return new FillGradient({
    type: 'radial',
    center: { x, y },
    innerRadius: 0,
    outerCenter: { x, y },
    outerRadius: r,
    scale,
    textureSpace: 'global',
    colorStops: stops.map(([offset, color]) => ({ offset, color })),
  });
}

/** Gradients in mock-up px; they scale with the Graphics transform, so one set serves every size. */
function makeShared() {
  return {
    railGold: linear(24, 0, 186.5, 0, [
      [0, 0x69542a],
      [0.1, 0x765d2d],
      [0.41, 0x9d7832],
      [0.59, 0xb28231],
      [0.87, 0xba852c],
      [1, 0xb6822c],
    ]),
    railTeal: linear(23.5, 0, 184.5, 0, [
      [0, 0x2c5c68],
      [0.1, 0x306470],
      [0.41, 0x387d8c],
      [0.6, 0x377380],
      [0.88, 0x376d79],
      [1, 0x346874],
    ]),
    topOuter: linear(-4, -4, 4.5, 4.5, [
      [0, 0x4a3824],
      [0.07, 0x685338],
      [0.15, 0x6e5129],
      [0.3, 0x705021],
      [0.55, 0x755620],
      [0.72, 0x805a1f],
      [0.82, 0x825d28],
      [0.9, 0x765528],
      [0.95, 0x644824],
      [1, 0x402711],
    ]),
    topInner: linear(9.75, 9.75, 17.25, 17.25, [
      [0, 0x423117],
      [0.07, 0x5a4824],
      [0.12, 0x614e23],
      [0.25, 0x5f481c],
      [0.45, 0x674c21],
      [0.6, 0x6b4e22],
      [0.7, 0x6e5223],
      [0.78, 0x715627],
      [0.85, 0x735b2b],
      [0.92, 0x736035],
      [1, 0x50411a],
    ]),
    topLamp: linear(-20.5, 0, -6, 0, [
      [0, 0x663203],
      [0.1, 0x975e19],
      [0.17, 0xa66f12],
      [0.24, 0xbc9120],
      [0.66, 0xb7921d],
      [0.72, 0xac770f],
      [0.8, 0xaf711a],
      [0.87, 0xa56120],
      [0.94, 0x79390b],
      [1, 0x531a00],
    ]),
    topLampCore: linear(-17, 0, -12, 0, [
      [0, 0xc99a26],
      [0.45, 0xc8bc3c],
      [1, 0xc8a02a],
    ]),
    bottomOuter: linear(-1.75, -1.75, 7.25, 7.25, [
      [0, 0x372c0e],
      [0.05, 0x5f5025],
      [0.1, 0x816e34],
      [0.16, 0x8c7531],
      [0.21, 0x8e7429],
      [0.26, 0x95782a],
      [0.4, 0x98732c],
      [0.55, 0x96742d],
      [0.65, 0x94772b],
      [0.74, 0x977825],
      [0.84, 0x978034],
      [0.89, 0xa89654],
      [0.95, 0x8f8554],
      [1, 0x484427],
    ]),
    bottomInner: linear(11.75, 11.75, 21.25, 21.25, [
      [0, 0x3c341d],
      [0.05, 0x5d4f2a],
      [0.1, 0x725c2b],
      [0.16, 0x7d5f2b],
      [0.32, 0x785825],
      [0.42, 0x785f27],
      [0.58, 0x755524],
      [0.74, 0x75581c],
      [0.8, 0x7e6427],
      [0.88, 0xa08a48],
      [0.94, 0x735d34],
      [1, 0x3a290b],
    ]),
    bottomHousing: linear(-26, 0, -3, 0, [
      [0, 0x0a1a2a],
      [0.25, 0x123250],
      [0.75, 0x17405e],
      [1, 0x0c2032],
    ]),
    bottomLamp: linear(-21.5, 0, -7.5, 0, [
      [0, 0x286884],
      [0.12, 0x4e9cb8],
      [0.26, 0x60aebe],
      [0.4, 0x84bec0],
      [0.62, 0x7ebebc],
      [0.8, 0x5caab2],
      [0.93, 0x4692aa],
      [1, 0x2e7090],
    ]),
    accent: linear(0, -3.5, 0, -0.5, [
      [0, 0x5f5626],
      [0.4, 0x91855a],
      [0.7, 0x746337],
      [1, 0x2d2706],
    ]),
    bracketTop: linear(0, 160.5, 0, 237, [
      [0, 0x96712a],
      [0.45, 0xba8a24],
      [1, 0xba8622],
    ]),
    sideBright: linear(-11.5, 0, -7.5, 0, [
      [0, 0x281c08],
      [0.15, 0x413318],
      [0.35, 0x948147],
      [0.6, 0xa89354],
      [0.85, 0x695228],
      [1, 0x241200],
    ]),
    sideLow: linear(-11, 0, -8, 0, [
      [0, 0x32301a],
      [0.3, 0x797154],
      [0.65, 0x787157],
      [1, 0x32301a],
    ]),
    sidePale: linear(-8, 0, -4.5, 0, [
      [0, 0x1a1408],
      [0.15, 0x36301a],
      [0.4, 0x898369],
      [0.65, 0x605a42],
      [1, 0x160f00],
    ]),
    sideGold: linear(-8, 0, -4.5, 0, [
      [0, 0x2a1c06],
      [0.2, 0x7c581e],
      [0.5, 0xb49042],
      [0.75, 0x856d25],
      [1, 0x241b00],
    ]),
    sideBlue: linear(-8, 0, -4, 0, [
      [0, 0x0d2120],
      [0.35, 0x22393f],
      [0.65, 0x21353c],
      [1, 0x051218],
    ]),
    sideHousing: linear(0, -34, 0, 36, [
      [0, 0x6e4a42],
      [0.14, 0x74463c],
      [0.3, 0x3a1c12],
      [0.75, 0x2e1810],
      [0.9, 0x1a1210],
      [1, 0x0c0a0a],
    ]),
    sideWindow: linear(-28, 0, -11, 0, [
      [0, 0x4d2410],
      [0.235, 0x8d3218],
      [0.353, 0xa44f0f],
      [0.41, 0xba751a],
      [0.47, 0xc6951f],
      [0.56, 0xc3a01a],
      [0.647, 0xc68d24],
      [0.735, 0xa85f10],
      [0.82, 0x9a3f10],
      [0.94, 0x6f240d],
      [1, 0x5a1f0d],
    ]),
    sideCore: linear(-21.5, 0, -16, 0, [
      [0, 0xba751a],
      [0.3, 0xc69c1e],
      [0.55, 0xc8a41e],
      [1, 0xc28a24],
    ]),
    glowOrange: radial(GLOW_TOP[0], GLOW_TOP[1], GLOW_TOP[2], [
      [0, 'rgba(255,150,50,0.42)'],
      [0.45, 'rgba(255,110,24,0.18)'],
      [1, 'rgba(255,90,10,0)'],
    ]),
    glowTeal: radial(GLOW_BOTTOM[0], GLOW_BOTTOM[1], GLOW_BOTTOM[2], [
      [0, 'rgba(90,220,240,0.4)'],
      [0.45, 'rgba(40,170,220,0.16)'],
      [1, 'rgba(20,120,200,0)'],
    ]),
    glowSide: radial(GLOW_SIDE[0], GLOW_SIDE[1], GLOW_SIDE[2], [
      [0, 'rgba(255,120,30,0.3)'],
      [0.6, 'rgba(255,90,20,0.1)'],
      [1, 'rgba(255,80,10,0)'],
    ]),
  };
}

/** Gradients that depend on the side length (playfield rows), in mock-up px. */
function makeLengthBound(hl: number) {
  const at = (v: number, from: number, to: number) =>
    Math.min(1, Math.max(0, (v - from) / (to - from)));
  const top = -FRAME_REACH.top;
  const bottom = hl + FRAME_REACH.bottom;
  return {
    chassis: linear(0, top, 0, bottom, [
      [0, 0x605841],
      [at(-32, top, bottom), 0x483d2b],
      [at(-28, top, bottom), 0x22180e],
      [at(60, top, bottom), 0x221e1e],
      [at(hl - 60, top, bottom), 0x1e1c1e],
      [at(hl + 22, top, bottom), 0x0e121a],
      [1, 0x080a10],
    ]),
    channel: linear(0, -27, 0, hl + 23.5, [
      [0, 0x0d0905],
      [0.5, 0x0b0a0a],
      [1, 0x05090e],
    ]),
    rimLine: linear(0, 52, 0, hl - 52, [
      [0, 0x5c4631],
      [at(161, 52, hl - 52), 0x4e4737],
      [at(241, 52, hl - 52), 0x292728],
      [at(hl - 247, 52, hl - 52), 0x2c2d2f],
      [at(hl - 150, 52, hl - 52), 0x312a22],
      [1, 0x214660],
    ]),
    bracketLow: linear(0, hl - 246.5, 0, hl - 167.5, [
      [0, 0xba8622],
      [0.55, 0xba8a24],
      [1, 0x96712a],
    ]),
  };
}

let shared: ReturnType<typeof makeShared> | null = null;
const lengthBound = new Map<number, ReturnType<typeof makeLengthBound>>();

/** Gold and turquoise arena frame traced from the mock-up, drawn with gradient metal. */
export class ArenaFrame {
  private g!: Graphics;
  private k = 1;
  private x = 0;
  private y = 0;
  private w = 0;
  private h = 0;

  /** Chassis, rails, side trim and lamp glow; goes under the grid. */
  drawBase(g: Graphics, x: number, y: number, w: number, h: number, cell: number): void {
    this.g = g;
    this.k = cell / DESIGN_CELL;
    this.x = x;
    this.y = y;
    this.w = w;
    this.h = h;
    const k = this.k;
    const wl = w / k;
    const hl = h / k;
    const s = (shared ??= makeShared());
    const key = Math.round(hl * 100);
    let l = lengthBound.get(key);
    if (!l) {
      l = makeLengthBound(hl);
      lengthBound.set(key, l);
    }

    g.setTransform(k, 0, 0, k, x, y);
    g.poly([
      0,
      -33,
      wl,
      -33,
      wl + 31,
      -2,
      wl + 31,
      hl - 3,
      wl - 2,
      hl + 30,
      2,
      hl + 30,
      -31,
      hl - 3,
      -31,
      -2,
    ]);
    g.fill(l.chassis);
    g.poly([
      3,
      -27,
      wl - 3,
      -27,
      wl + 20.5,
      -3.5,
      wl + 20.5,
      hl + 0.5,
      wl - 2.5,
      hl + 23.5,
      2.5,
      hl + 23.5,
      -20.5,
      hl + 0.5,
      -20.5,
      -3.5,
    ]);
    g.fill(l.channel);
    g.rect(0, 0, wl, hl);
    g.fill({ color: PALETTE.boardInner });
    g.resetTransform();

    for (const dir of [1, -1] as const) {
      this.side(dir, hl, s, l);
      this.topRail(dir, s);
      this.bottomRail(dir, s);
      this.at(dir, 1);
      this.glow(GLOW_TOP, s.glowOrange);
      this.at(dir, -1);
      this.glow(GLOW_BOTTOM, s.glowTeal);
      g.resetTransform();
    }
  }

  /** Corner pieces cover the playfield corners, so they go over the grid. */
  drawCorners(): void {
    const g = this.g;
    const s = (shared ??= makeShared());
    for (const dir of [1, -1] as const) {
      this.at(dir, 1);
      g.poly([0, 0, 20, 0, 0, 20]);
      g.fill({ color: 0x110b05 });
      this.metal(TOP_OUTER, s.topOuter, GOLD_EDGE);
      this.lit(TOP_OUTER_LIT, 0xa08860, 0.6);
      g.poly(TOP_LAMP_HOUSING as number[]);
      g.fill(s.topLamp);
      g.roundRect(-17, 19.5, 5, 28.5, 2.5);
      g.fill(s.topLampCore);
      this.metal(TOP_INNER, s.topInner, GOLD_EDGE);

      this.at(dir, -1);
      g.poly([0, 0, 23.5, 0, 0, 23.5]);
      g.fill({ color: 0x0a0c0e });
      g.roundRect(-26, 20, 23, 48, 4);
      g.fill(s.bottomHousing);
      g.roundRect(-21.5, 26.5, 14, 38, 5.5);
      g.fill(s.bottomLamp);
      g.roundRect(-16.5, 30.5, 4.5, 29, 2.2);
      g.fill({ color: 0xb4d4d0, alpha: 0.8 });
      this.metal(BOTTOM_OUTER, s.bottomOuter, GOLD_EDGE);
      this.metal(BOTTOM_INNER, s.bottomInner, GOLD_EDGE);
      this.lit(BOTTOM_INNER_LIT, 0x99966a, 0.7);
      g.rect(36.5, -3.5, 14, 3);
      g.fill(s.accent);
      g.resetTransform();
    }
  }

  /** Mirror into corner space: dir picks left (1) or right (-1), end picks top (1) or bottom (-1). */
  private at(dir: 1 | -1, end: 1 | -1): void {
    this.g.setTransform(
      this.k * dir,
      0,
      0,
      this.k * end,
      dir > 0 ? this.x : this.x + this.w,
      end > 0 ? this.y : this.y + this.h,
    );
  }

  private glow([u, v, r]: readonly [number, number, number], fill: FillGradient): void {
    this.g.rect(u - r, v - r, r * 2, r * 2);
    this.g.fill(fill);
  }

  private metal(pts: Pts, fill: FillGradient, edge: number): void {
    const g = this.g;
    g.poly(pts as number[]);
    g.fill(fill);
    g.poly(pts as number[]);
    g.stroke({ color: edge, width: Math.max(0.8, this.k * 1.1), alpha: 0.85, join: 'miter' });
  }

  private lit(pts: Pts, color: number, alpha: number, width = 1.1): void {
    const g = this.g;
    g.moveTo(pts[0] ?? 0, pts[1] ?? 0);
    for (let i = 2; i < pts.length; i += 2) g.lineTo(pts[i] ?? 0, pts[i + 1] ?? 0);
    g.stroke({ color, width: Math.max(0.7, this.k * width), alpha, cap: 'butt' });
  }

  private side(
    dir: 1 | -1,
    hl: number,
    s: ReturnType<typeof makeShared>,
    l: ReturnType<typeof makeLengthBound>,
  ): void {
    const g = this.g;
    const k = this.k;
    const ex = dir > 0 ? this.x : this.x + this.w;
    g.setTransform(k * dir, 0, 0, k, ex, this.y);

    g.rect(-22.25, 52, 2.5, hl - 104);
    g.fill(l.rimLine);
    g.rect(-11.5, 51, 4, 110);
    g.fill(s.sideBright);
    g.rect(-11, hl - 159, 3, 93);
    g.fill(s.sideLow);
    g.rect(-8, 188, 3.5, hl - 247 - 188);
    g.fill(s.sidePale);
    g.rect(-8, 188, 3.5, 30);
    g.fill(s.sideGold);
    g.rect(-8, hl - 225, 4, 57);
    g.fill(s.sideBlue);
    g.rect(-15, 238, 1.5, hl - 248 - 238);
    g.fill({ color: 0x423e42, alpha: 0.9 });
    for (const v of [76, 141, hl - 151, hl - 87]) {
      g.rect(-17, v, 2, 3.5);
      g.fill({ color: 0x968c78 });
    }

    this.metal(BRACKET_TOP, s.bracketTop, GOLD_EDGE);
    this.lit(BRACKET_TOP_LIT, 0xd8b868, 0.55);
    const low = BRACKET_LOW.map((n, i) => (i % 2 ? hl + n : n));
    const lowLit = BRACKET_LOW_LIT.map((n, i) => (i % 2 ? hl + n : n));
    this.metal(low, l.bracketLow, GOLD_EDGE);
    this.lit(lowLit, 0xd8b868, 0.55);

    const lampY = this.y + hl * SIDE_LAMP_AT * k;
    g.setTransform(k * dir, 0, 0, k * GLOW_SIDE_STRETCH, ex, lampY);
    this.glow(GLOW_SIDE, s.glowSide);
    g.setTransform(k * dir, 0, 0, k, ex, lampY);
    g.poly(SIDE_HOUSING as number[]);
    g.fill(s.sideHousing);
    g.poly(SIDE_HOUSING as number[]);
    g.stroke({ color: 0x8a6a60, width: Math.max(0.7, k * 0.9), alpha: 0.3 });
    g.rect(-28, -20.5, 17, 41);
    g.fill(s.sideWindow);
    g.roundRect(-21.5, -21.5, 5.5, 43, 2.7);
    g.fill(s.sideCore);
    g.resetTransform();
  }

  private topRail(dir: 1 | -1, s: ReturnType<typeof makeShared>): void {
    this.at(dir, 1);
    this.g.rect(60, -14.5, 90, 2);
    this.g.fill({ color: 0x16302f, alpha: 0.9 });
    this.metal(TOP_RAIL, s.railGold, GOLD_EDGE);
    this.lit(TOP_RAIL_LIT, 0xe0c27a, 0.45);
    this.lit(TOP_SEAM, 0x241808, 0.9, 1.3);
    this.g.resetTransform();
  }

  private bottomRail(dir: 1 | -1, s: ReturnType<typeof makeShared>): void {
    this.at(dir, -1);
    this.metal(BOTTOM_RAIL, s.railTeal, TEAL_EDGE);
    this.lit(BOTTOM_RAIL_LIT, 0xa8c4c2, 0.95, 1.8);
    this.lit(BOTTOM_SEAM, 0x06161c, 0.9, 1.3);
    this.g.resetTransform();
  }
}
